import { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  BreadcrumbComponent,
  Button,
  Caret,
  Subtitle,
  Title,
} from '../../components/shared';
import { IMusselLineDetail } from '../../entities/farms.entities';
import prevIcon from '../../images/prev-image.png';
import nextIcon from '../../images/next-image.png';
import useMenuHandler from '../../lib/useMenuHandler';
import LineTagButton from '../../components/lines/LineTagButton';
import getBreadcrumbMenu from '../../lib/BreadcrumbMenu';
import {
  ICycleResource,
  IMusselHarvest,
} from '../../entities/growing.entities';
import HarvestPredictButton from '../../components/lines/HarvestPredictButton';
import SeedingTrackModal from '../../components/track-modals/SeedingTrackModal';
import AddTrackStatusModal from '../../components/track-modals/AddTrackStatusModal';
import { selectLang } from '../../store/ui/ui.selector';
import { translate, transUnit } from '../../lib/lang.helper';
import {
  selectAllFarms,
  SelectIsEditable,
  SelectIsFarmable,
} from '../../store/extra/extra.selector';
import metaIcon from '../../images/history_icon.png';
import HarvestDateModal from '../../components/farm-modals/HarvestDateModal';
import DotsMenu, {
  IMenu,
} from '../../components/shared/dropdown-menu/DotsMenu';
import MusselLineHeader from '../../components/lines-headers/MusselLineHeader';
import LineHeadersModal from '../../components/lines-headers/LineHeadersModal';
import { displayValueUnit } from '../../lib/common.helpers';
import { selectAccount, selectIsAdmin } from '../../store/auth/auth.selector';
import LineCropTypeModal from '../../components/lines/LineCropTypeModal';
import LineManageButtonsView from './LineManageButtonsView';

interface Props {
  lineData: IMusselLineDetail;
  loadLine: () => void;
  cyclePart: ICycleResource | null;
  isPrevEnabled: boolean;
  isNextEnabled: boolean;
  onClickPrevGroup: () => void;
  onClickNextGroup: () => void;
  showEditLineModal: () => void;
  onDeleteClick: () => void;
  onSeedingClick: () => void;
  onAssessmentClick: () => void;
  onCatchSpatClick: () => void;
  onHarvestClick: () => void;
  onReturnClick: () => void;
  onMaintenanceClick: () => void;
  onFloatingManageClick: () => void;
  onExtraSeedClick: () => void;
  onMetaEditClick: () => void;
  checkedHistoryView: number;
  onHistoryViewClick: () => void;
  onWasteClick: () => void;
}

const MusselLineDesktop = ({
  lineData,
  loadLine,
  cyclePart,
  onClickPrevGroup,
  onClickNextGroup,
  isPrevEnabled,
  isNextEnabled,
  showEditLineModal,
  onDeleteClick,
  onSeedingClick,
  onAssessmentClick,
  onCatchSpatClick,
  onHarvestClick,
  onReturnClick,
  onMaintenanceClick,
  onFloatingManageClick,
  onExtraSeedClick,
  onMetaEditClick,
  checkedHistoryView,
  onHistoryViewClick,
  onWasteClick,
}: Props) => {
  const lang = useSelector(selectLang);
  const isEditable = SelectIsEditable(lineData.farm_id);
  const isFarmable = SelectIsFarmable(lineData.farm_id);
  const { redirectToLine } = useMenuHandler();
  const curFarm = useSelector(selectAllFarms).find(
    x => x.id === lineData.farm_id,
  );
  const account = useSelector(selectAccount);
  const isAdmin = useSelector(selectIsAdmin);
  const unitType = account?.display_unit;
  const lines = curFarm?.lines ?? [];
  const mainSeed = cyclePart?.main_seed;
  const cur = lines.findIndex(t => t.id === lineData.id) ?? -1;
  const prev = (cur - 1 + (lines.length ?? 0)) % (lines.length ?? 0);
  const next = (cur + 1) % (lines.length ?? 0);

  const breadcrumbItems = getBreadcrumbMenu('FARM_LINE', lang, {
    FARM_ID: lineData.farm_id,
    LINE_ID: lineData.id,
    FARM_NAME: lineData?.farm_name,
    LINE_NAME: lineData?.line_name,
  }).slice(0, -1);

  const [showLineSpat, setShowLineSpat] = useState(false);
  const [trackSeedings, setTrackSeedings] = useState<number[]>();
  const [qrSeed, setQrSeed] = useState<{
    farmID: number;
    lineID: number;
    seedingID: number;
    harvests: IMusselHarvest[];
  }>();
  const [visEstDate, setVisEstDate] = useState(false);
  const [visEditHeader, setVisEditHeader] = useState(false);
  const [visCropType, setVisCropType] = useState(false);

  const { bagsQuantity, harvestedLength } = useMemo(
    () => ({
      bagsQuantity: lineData.growing_cycle?.total_harvested_bags ?? 0,
      harvestedLength: lineData.growing_cycle?.total_harvested_length ?? 0,
    }),
    [lineData],
  );
  const getMenuItems = () => {
    let result: IMenu[] = [
      {
        label: translate(lang, 'Edit'),
        onClick: showEditLineModal,
      },
    ];
    if (mainSeed) {
      result = [
        ...result,
        {
          label: translate(lang, 'View Scanned QR'),
          onClick: () => setTrackSeedings([mainSeed.id]),
        },
        {
          label: translate(lang, 'Add Scanned QR'),
          onClick: () =>
            setQrSeed({
              farmID: lineData.farm_id,
              lineID: lineData.id,
              seedingID: mainSeed.id,
              harvests: cyclePart.harvests as IMusselHarvest[],
            }),
        },
        {
          label: translate(lang, 'Change harvest date'),
          onClick: () => setVisEstDate(true),
        },
      ];
    }
    result = [
      ...result,
      {
        label: translate(lang, 'Delete'),
        onClick: onDeleteClick,
        disabled: !isAdmin,
      },
      {
        label: translate(lang, 'Edit line headers'),
        onClick: () => setVisEditHeader(true),
      },
    ];
    if (!lineData.growing_cycle) {
      result = [
        ...result,
        {
          label: translate(lang, 'Change crop type'),
          onClick: () => setVisCropType(true),
        },
      ];
    }
    return result;
  };

  return (
    <div className='farm-line-desktop-template'>
      <div className='pt-28 pb-28 d-flex justify-between align-center'>
        <div className='d-flex' style={{ alignItems: 'baseline' }}>
          <BreadcrumbComponent items={breadcrumbItems} />
          {lines && (
            <div className='ml-17'>
              <span
                className='prev-link'
                onClick={() => redirectToLine(lineData.farm_id, lines[prev].id)}
              >
                <img src={prevIcon} alt='prev' />
              </span>
              <span className='line-name'>
                {`${transUnit(lang, 'line', curFarm?.structure)} ${
                  lineData.line_name
                }`}
              </span>
              <span
                className='next-link'
                onClick={() => redirectToLine(lineData.farm_id, lines[next].id)}
              >
                <img src={nextIcon} alt='next' />
              </span>
            </div>
          )}
        </div>
        <div className='d-flex justify-between align-center'>
          {lineData.growing_cycle?.taxonomy_groups &&
            lineData.growing_cycle.taxonomy_groups.length > 0 && (
              <Button
                color='green'
                size={1}
                width='middle'
                type='bordered'
                className='mr-8 mxw-270'
              >
                {`${translate(lang, 'Taxonomies')}: ${lineData.growing_cycle.taxonomy_groups.map(x => x.name).join(', ')}`}
              </Button>
            )}
          <div className='d-flex justify-between align-center white-card-small pt-3 pb-3'>
            <Button
              color='blue'
              size={3}
              width='default'
              type='transparent'
              className='mr-26'
              name='prev'
              onClick={onClickPrevGroup}
              disabled={!isPrevEnabled}
              onlyIconDisabled
            >
              <Caret color='#5A607F' direction='left' />
            </Button>
            <Subtitle
              size={4}
              color='black'
              align='left'
              fontWeight={500}
              disabled={!mainSeed}
            >
              {mainSeed?.season_name ?? translate(lang, 'Empty')}
            </Subtitle>
            <Button
              color='blue'
              size={3}
              width='default'
              type='transparent'
              className='ml-26'
              name='next'
              onClick={onClickNextGroup}
              disabled={!isNextEnabled}
              onlyIconDisabled
            >
              <Caret color='#5A607F' direction='right' />
            </Button>
          </div>
          <Button
            color='blue'
            size={0}
            width='default'
            type='bordered'
            className='ml-8'
            iconOnly
            onClick={onMetaEditClick}
            disabled={!isEditable}
          >
            <img src={metaIcon} alt='meta-icon' style={{ width: 40 }} />
          </Button>
          <Button
            color='blue'
            size={1}
            width='middle'
            type='bordered'
            className='ml-16'
            onClick={onHarvestClick}
            disabled={!mainSeed?.is_growing || !isFarmable}
          >
            {translate(lang, 'Harvest Complete')}
          </Button>
          <Button
            color='red'
            size={1}
            width='middle'
            type='bordered'
            onClick={onWasteClick}
            disabled={!mainSeed?.is_growing || !isFarmable}
          >
            {translate(lang, 'Stripped To Waste')}
          </Button>
          {!!account?.have_customers && (
            <Button
              color='blue'
              size={1}
              width='middle'
              type='bordered'
              onClick={onReturnClick}
              disabled={!mainSeed?.is_growing || !isFarmable}
            >
              {translate(lang, 'Spat Harvest')}
            </Button>
          )}
        </div>
      </div>
      <div className='white-card pt-28 pr-16 pb-28 pl-24 mb-16'>
        <div className='d-flex justify-between'>
          <div>
            <Title size={5} color='black-3' align='default' fontWeight={500}>
              {`${transUnit(lang, 'line', curFarm?.structure)} ${
                lineData.line_name
              }`}
            </Title>
            <div style={{ marginTop: '7px' }}>
              <div
                className='--spat-count'
                onClick={e => setShowLineSpat(true)}
              >
                {translate(
                  lang,
                  'Last Spat Count: %s',
                  lineData.last_spat_count ?? 0,
                )}
              </div>
            </div>
            {!!lineData.growing_cycle?.last_harvest ? (
              <div style={{ marginTop: '20px' }}>
                <Link
                  to={`/harvest/${lineData.growing_cycle.last_harvest.id}`}
                  style={{
                    fontStyle: 'italic',
                    textDecorationLine: 'underline',
                  }}
                >
                  <Subtitle
                    size={5}
                    color='gray'
                    align='default'
                    fontWeight={400}
                  >
                    {translate(lang, '_line_partially_harvested')}
                  </Subtitle>
                </Link>
                <Subtitle
                  size={5}
                  color='#000'
                  align='default'
                  fontWeight={500}
                >
                  {translate(
                    lang,
                    '_harvested_bags_so_far',
                    displayValueUnit(harvestedLength, unitType, 'distance_m'),
                    bagsQuantity,
                  )}
                </Subtitle>
              </div>
            ) : (
              !!lineData.last_harvest && (
                <div style={{ marginTop: '10px' }}>
                  <Link
                    to={`/harvest/${lineData.last_harvest.id}`}
                    style={{
                      fontStyle: 'italic',
                      textDecorationLine: 'underline',
                    }}
                  >
                    <Subtitle
                      size={5}
                      color='gray'
                      align='default'
                      fontWeight={400}
                    >
                      {translate(lang, 'View last harvest')}
                    </Subtitle>
                  </Link>
                </div>
              )
            )}
            <div className='mt-17'>
              <LineTagButton lineData={lineData} loadLine={loadLine} />
            </div>
          </div>
          <MusselLineHeader line={lineData} cycle={cyclePart} />
          {isEditable && <DotsMenu items={getMenuItems()} />}
        </div>
        {!!lineData.growing_cycle?.predicted_date_harvest && (
          <div style={{ textAlign: 'right' }}>
            <HarvestPredictButton lineData={lineData} />
          </div>
        )}
      </div>
      <LineManageButtonsView
        lineData={lineData}
        cyclePart={cyclePart}
        checkedHistoryView={checkedHistoryView}
        onHistoryViewClick={onHistoryViewClick}
        onFloatingManageClick={onFloatingManageClick}
        onSeedingClick={onSeedingClick}
        onAssessmentClick={onAssessmentClick}
        onCatchSpatClick={onCatchSpatClick}
        onMaintenanceClick={onMaintenanceClick}
        onExtraSeedClick={onExtraSeedClick}
        loadLine={loadLine}
        showLineSpat={showLineSpat}
        setShowLineSpat={setShowLineSpat}
      />
      {!!trackSeedings && (
        <SeedingTrackModal
          visible={true}
          seedingIDs={trackSeedings}
          onClose={() => setTrackSeedings(undefined)}
        />
      )}
      {!!qrSeed && (
        <AddTrackStatusModal
          visible={true}
          seedingID={qrSeed.seedingID}
          lineID={qrSeed.lineID}
          harvests={qrSeed.harvests}
          onClose={() => setQrSeed(undefined)}
        />
      )}
      {visEstDate && !!cyclePart && (
        <HarvestDateModal
          visible={true}
          cycleData={cyclePart}
          onClose={() => setVisEstDate(false)}
          onUpdate={loadLine}
          cropType={'MUSSEL'}
        />
      )}
      {visEditHeader && (
        <LineHeadersModal
          type='MUSSEL'
          visible={true}
          onClose={() => setVisEditHeader(false)}
          line={lineData}
          farm={curFarm}
        />
      )}
      {visCropType && (
        <LineCropTypeModal
          visible={true}
          lineData={lineData}
          onClose={() => setVisCropType(false)}
          loadLine={loadLine}
        />
      )}
    </div>
  );
};

export default MusselLineDesktop;
